import React, { useState, useEffect } from "react";
import { Hash, AlertCircle, Users, CheckCircle } from "lucide-react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useJoinGroup } from "../api/groups/useJoinGroup";
import { usePreviewJoinGroup } from "../api/groups/usePreviewJoinGroup";
import { useAppRouter } from "../hooks/useAppRouter";
import { useTranslation } from "../i18n/TranslationContext";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useGetExtra } from "../api/extras/useGetExtra";

export function GroupJoin() {
	const { navigateDirectly, updateUserCompletionState } = useApp();
	const router = useAppRouter();
	const { joinGroup, loading: apiLoading, error: apiError } = useJoinGroup();
	const {
		previewJoinGroup,
		loading: previewLoading,
		error: previewError,
		previewData,
		clearPreview,
	} = usePreviewJoinGroup();
	const { t } = useTranslation();

	// Get current user data
	const { user } = useCurrentUser();
	const { extra } = useGetExtra(user?.extraId);

	const [groupNumber, setGroupNumber] = useState("");
	const [error, setError] = useState<string>("");
	const [joinedGroup, setJoinedGroup] = useState<any>(null);
	const [showSuccessMessage, setShowSuccessMessage] = useState(false);

	const handleBack = () => {
		navigateDirectly("app-group-select");
	};

	const handleGroupNumberChange = (value: string) => {
		// Only allow numbers and limit to 8 digits
		const numericValue = value.replace(/\D/g, "").slice(0, 8);
		setGroupNumber(numericValue);
		setError("");
		setJoinedGroup(null);
		setShowSuccessMessage(false);

		// Clear preview if number is not complete
		if (numericValue.length < 8) {
			clearPreview();
		}
	};

	// Preview group when 8 digits are entered
	useEffect(() => {
		if (groupNumber.length === 8 && !showSuccessMessage) {
			previewJoinGroup(groupNumber);
		}
	}, [groupNumber, showSuccessMessage, previewJoinGroup]);

	const handleJoinGroup = async () => {
		if (!groupNumber.trim() || groupNumber.length !== 8) {
			setError("Bitte geben Sie eine gültige 8-stellige Gruppennummer ein.");
			return;
		}

		// Validation checks
		// Check if profile is complete
		if (extra?.profile_status !== "ready_to_work") {
			setError(
				"Sie müssen Ihr Profil vollständig ausfüllen, bevor Sie einer Gruppe beitreten können."
			);
			return;
		}

		// Check if code of conduct is accepted
		if (!extra?.code_of_conduct) {
			setError(
				"Sie müssen den Verhaltenskodex akzeptieren, bevor Sie einer Gruppe beitreten können."
			);
			return;
		}

		setError("");

		const result = await joinGroup(parseInt(groupNumber));

		if (result) {
			// Store the joined group data
			setJoinedGroup(result);
			setShowSuccessMessage(true);

			// Update completion state
			updateUserCompletionState({ group_member: true });

			// Navigate to group display screen after a short delay to show success
			setTimeout(() => {
				router.navigate("app-group");
			}, 1500);
		} else if (apiError) {
			// Handle specific error cases
			if (apiError.includes("not found")) {
				setError("Die eingegebene Gruppennummer wurde nicht gefunden.");
			} else if (apiError.includes("full")) {
				setError(
					"Diese Gruppe ist bereits voll. Es können maximal 4 Personen in einer Gruppe sein."
				);
			} else {
				setError(apiError);
			}
		}
	};

	return (
		<MainLayout showBack={true} onBack={handleBack}>
			<div className="space-y-6">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="w-16 h-16 bg-[var(--ucm-success-green)] rounded-full flex items-center justify-center mx-auto mb-4">
						<Hash size={32} className="text-white" />
					</div>
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
						{t("groupJoin.title")}
					</h1>
					<p className="text-[var(--ucm-text-medium)] px-4">
						{t("groupJoin.subtitle")}
					</p>
				</div>

				{/* Join Form */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] space-y-6">
					{/* Group Number Input */}
					<div>
						<label
							htmlFor="groupNumber"
							className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
						>
							{t("groupJoin.groupNumber")} {t("groupJoin.groupNumberRequired")}
						</label>
						<div className="relative">
							<input
								type="text"
								id="groupNumber"
								value={groupNumber}
								onChange={(e) => handleGroupNumberChange(e.target.value)}
								className="w-full px-4 py-3 pr-12 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-success-green)] transition-colors text-center text-lg font-mono tracking-wider"
								placeholder={t("groupJoin.placeholder")}
								maxLength={8}
								disabled={apiLoading || previewLoading}
							/>
							<div className="absolute right-4 top-1/2 transform -translate-y-1/2">
								{apiLoading ||
									(previewLoading && (
										<div className="w-5 h-5 border-2 border-[var(--ucm-success-green)] border-t-transparent rounded-full animate-spin"></div>
									))}
								{showSuccessMessage && !apiLoading && (
									<CheckCircle
										size={20}
										className="text-[var(--ucm-success-green)]"
									/>
								)}
							</div>
						</div>
						<p className="text-xs text-[var(--ucm-text-light)] mt-2">
							{t("groupJoin.helpText")}
						</p>
					</div>
				</div>

				{/* Group Preview - Show when group data is loaded */}
				{previewData && !showSuccessMessage && (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<div className="flex items-start space-x-3 mb-4">
							<CheckCircle
								size={20}
								className="text-[var(--ucm-success-green)] mt-0.5 flex-shrink-0"
							/>
							<h3 className="font-semibold text-[var(--ucm-text-dark)]">
								{t("groupJoin.previewTitle")}
							</h3>
						</div>

						<div className="bg-[var(--ucm-light-green)] border border-[var(--ucm-success-green)] rounded-xl p-4 mb-4">
							<div className="flex items-start space-x-3">
								<div className="w-12 h-12 bg-[var(--ucm-success-green)] rounded-full flex items-center justify-center flex-shrink-0">
									<Users size={24} className="text-white" />
								</div>
								<div className="flex-1">
									<h4 className="font-semibold text-[var(--ucm-dark-green)] mb-1">
										{previewData.group.name}
									</h4>
									{previewData.group.description && (
										<p className="text-sm text-[var(--ucm-dark-green)] mb-2">
											{previewData.group.description}
										</p>
									)}
									<div className="text-xs text-[var(--ucm-dark-green)]">
										{t("groupJoin.groupCode")}: {previewData.group.group_code}
									</div>
								</div>
							</div>
						</div>

						{/* Affected Events Warning */}
						{previewData.affected_events &&
							previewData.affected_events.total_count > 0 && (
								<div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
									<div className="flex items-start space-x-3 mb-3">
										<AlertCircle
											size={20}
											className="text-yellow-600 mt-0.5 flex-shrink-0"
										/>
										<div className="flex-1">
											<h4 className="font-semibold text-yellow-800 mb-2">
												Termine werden betroffen sein
											</h4>
											<p className="text-sm text-yellow-700 mb-3">
												Der Beitritt zu dieser Gruppe wirkt sich auf Ihre
												bestehende Terminanmeldung aus:
											</p>

											{/* Applied Events */}
											{previewData.affected_events.applied_events.length >
												0 && (
												<div className="mb-3">
													<p className="text-xs font-semibold text-yellow-800 mb-2">
														Werden zurückgezogen (
														{previewData.affected_events.applied_events.length}
														):
													</p>
													<div className="space-y-1 max-h-32 overflow-y-auto">
														{previewData.affected_events.applied_events.map(
															(event) => (
																<div
																	key={event.event_id}
																	className="text-sm text-yellow-700 bg-yellow-100 rounded px-2 py-1"
																>
																	{new Date(
																		event.event_date
																	).toLocaleDateString("de-DE")}{" "}
																	({event.start_time} - {event.end_time})
																</div>
															)
														)}
													</div>
												</div>
											)}

											{/* Booked Events */}
											{previewData.affected_events.booked_events.length > 0 && (
												<div>
													<p className="text-xs font-semibold text-yellow-800 mb-2">
														Werden storniert (
														{previewData.affected_events.booked_events.length}):
													</p>
													<div className="space-y-1 max-h-32 overflow-y-auto">
														{previewData.affected_events.booked_events.map(
															(event) => (
																<div
																	key={event.event_id}
																	className="text-sm text-yellow-700 bg-yellow-100 rounded px-2 py-1"
																>
																	{event.event_type} -{" "}
																	{new Date(
																		event.event_date
																	).toLocaleDateString("de-DE")}{" "}
																	({event.start_time} - {event.end_time})
																</div>
															)
														)}
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							)}

						<p className="text-sm text-[var(--ucm-text-medium)]">
							{previewData.affected_events &&
							previewData.affected_events.total_count > 0
								? "Klicken Sie auf 'Gruppe beitreten', um zu bestätigen und mit diesen Änderungen fortzufahren."
								: t("groupJoin.previewMessage")}
						</p>
					</div>
				)}

				{/* Success Message */}
				{showSuccessMessage && joinedGroup && (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<div className="flex items-start space-x-3 mb-4">
							<CheckCircle
								size={20}
								className="text-[var(--ucm-success-green)] mt-0.5 flex-shrink-0"
							/>
							<h3 className="font-semibold text-[var(--ucm-text-dark)]">
								{t("groupJoin.successTitle")}
							</h3>
						</div>

						<div className="bg-[var(--ucm-light-green)] border border-[var(--ucm-success-green)] rounded-xl p-4">
							<div className="flex items-start space-x-3">
								<div className="w-12 h-12 bg-[var(--ucm-success-green)] rounded-full flex items-center justify-center flex-shrink-0">
									<Users size={24} className="text-white" />
								</div>
								<div className="flex-1">
									<h4 className="font-semibold text-[var(--ucm-dark-green)] mb-1">
										{joinedGroup.name || joinedGroup.group_name}
									</h4>
									{joinedGroup.description && (
										<p className="text-sm text-[var(--ucm-dark-green)] mb-2">
											{joinedGroup.description}
										</p>
									)}
									{joinedGroup.members && (
										<div className="text-xs text-[var(--ucm-dark-green)]">
											<span>
												{joinedGroup.members.length}{" "}
												{joinedGroup.members.length === 1
													? t("groupJoin.member")
													: t("groupJoin.members")}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
						<p className="text-sm text-[var(--ucm-text-medium)] mt-4 text-center">
							{t("groupJoin.redirecting")}
						</p>
					</div>
				)}

				{/* Error Display - Show appropriate message based on error type */}
				{(error || previewError) && (
					<div className="bg-red-50 rounded-2xl p-6 border border-red-200">
						{(error || previewError || "").includes("not found") ? (
							// Group not found - friendly message
							<div className="text-center">
								<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<span className="text-3xl">❌</span>
								</div>
								<h3 className="font-semibold text-[var(--ucm-text-dark)] mb-2">
									{t("groupJoin.errors.notFoundTitle")}
								</h3>
								<p className="text-sm text-[var(--ucm-text-medium)] mb-4">
									{t("groupJoin.errors.notFoundMessage")}{" "}
									<strong>{groupNumber}</strong>
								</p>
								<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-xl p-3">
									<p className="text-xs text-[var(--ucm-dark-blue)]">
										💡 {t("groupJoin.errors.notFoundHint")}
									</p>
								</div>
							</div>
						) : (error || previewError || "").includes("full") ? (
							// Group is full - informative message
							<div className="text-center">
								<div className="w-16 h-16 bg-[var(--ucm-error-red)] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
									<Users size={32} className="text-[var(--ucm-error-red)]" />
								</div>
								<h3 className="font-semibold text-[var(--ucm-text-dark)] mb-2">
									{t("groupJoin.errors.fullTitle")}
								</h3>
								<p className="text-sm text-[var(--ucm-text-medium)]">
									{t("groupJoin.errors.fullMessage")}
								</p>
								<p className="text-xs text-[var(--ucm-text-light)] mt-2">
									{t("groupJoin.errors.fullHint")}
								</p>
							</div>
						) : (
							// Generic error
							<div className="flex items-start space-x-3">
								<AlertCircle
									size={20}
									className="text-[var(--ucm-error-red)] mt-0.5 flex-shrink-0"
								/>
								<p className="text-sm text-[var(--ucm-error-red)]">
									{error || previewError}
								</p>
							</div>
						)}
					</div>
				)}

				{/* Action Buttons */}
				<div className="flex space-x-3">
					<button
						onClick={handleBack}
						disabled={apiLoading || previewLoading}
						className="flex-1 h-12 bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] font-semibold rounded-full hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{t("groupJoin.cancel")}
					</button>
					<button
						onClick={handleJoinGroup}
						disabled={
							apiLoading ||
							groupNumber.length !== 8 ||
							showSuccessMessage ||
							(previewData?.group?.is_full ?? false)
						}
						className="flex-1 h-12 bg-[var(--ucm-success-green)] text-white font-semibold rounded-full hover:bg-[var(--ucm-dark-green)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{apiLoading || previewLoading
							? t("groupJoin.joining")
							: previewData?.group?.is_full
								? t("groupJoin.groupFull")
								: t("groupJoin.joinGroup")}
					</button>
				</div>

				{/* Information */}
				<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-2xl p-4">
					<div className="flex items-start space-x-3">
						<span className="text-xl flex-shrink-0">💡</span>
						<div>
							<h4 className="font-semibold text-[var(--ucm-dark-blue)] mb-1">
								{t("groupJoin.infoTitle")}
							</h4>
							<ul className="text-sm text-[var(--ucm-dark-blue)] space-y-1">
								<li>• {t("groupJoin.infoPoints.member")}</li>
								<li>• {t("groupJoin.infoPoints.leader")}</li>
								<li>• {t("groupJoin.infoPoints.leave")}</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</MainLayout>
	);
}
